import coreIconLabMetadata from '@atlaskit/icon-lab/metadata';
import { metaDataWithPackageLoader as objectMetadata } from '@atlaskit/icon-object/metadata';
import { coreIconMetadata } from '@atlaskit/icon/metadata';

import { type AllIconsList, type IconData, type NewCoreIconData } from './types';

export const globalCoreIconImportPrefix = '@atlaskit/icon/core';
export const iconLabCoreIconImportPrefix = '@atlaskit/icon-lab/core';
export const iconObjectImportPrefix = '@atlaskit/icon-object/glyph';

export const sortIntoCategories: (icons: IconData[]) => AllIconsList = (icons: IconData[]) => {
	// e.g. @atlaskit/icon-object/glyph/branch/24
	const categorisedIcons = {
		'16': [],
		'24': [],
	} as AllIconsList;
	const sizes = ['16', '24'];
	const numbersRegex = /\d+/g;
	icons.forEach((icon) => {
		const size = icon.componentName.match(numbersRegex)?.[0]; // componentName example = Branch24Icon
		if (size && sizes.includes(size)) {
			categorisedIcons[size].push(icon);
		}
	});
	return categorisedIcons;
};

// example: media-services/vid-play
// should become Vid play
export const getIconName: (name: string) => string = (name: string): string => {
	// remove prefix (e.g. media-services/)
	const nameAsArray = name.split('/');
	if (nameAsArray.length > 1) {
		if (nameAsArray[1] === '16' || nameAsArray[1] === '24') {
			// for object icons: e.g. blog/24, where the number denotes the size
			nameAsArray.pop();
		} else {
			nameAsArray.shift();
		}
	}
	let iconName = nameAsArray.join('');

	// replace any hyphens with spaces
	// and capitalise only the first letter
	if (iconName) {
		iconName = iconName.replace(/-/g, ' ');
		//replaced '-' with /-/g. removes all instances of hyphens rather than just the first
		iconName = iconName.charAt(0).toUpperCase() + iconName.slice(1);
	}

	return iconName;
};

export const getComponentName: (name: string) => string = (name: string): string => {
	return name
		.split(/\W/)
		.map((part) => `${part[0].toUpperCase()}${part.slice(1)}`)
		.join('')
		.concat('Icon');
};

// we use this to cache the result
let objectIconsPromise: Promise<unknown> | null = null;

export const getObjectIcons: () => Promise<unknown> = (): Promise<unknown> => {
	if (!objectIconsPromise) {
		objectIconsPromise = Promise.all(
			Object.keys(objectMetadata).map(async (name: string) => {
				try {
					const icon = await objectMetadata[name].packageLoader();
					return { name, icon: icon.default };
				} catch {
					throw new Error("Can't find name" + name);
				}
			}),
		)
			.then((newData) =>
				newData
					.map((icon) => {
						const metadata = objectMetadata[icon.name];
						return {
							[icon.name]: {
								keywords: metadata.keywords,
								component: icon.icon,
								componentName: metadata.componentName,
								packageName: metadata.package,
								iconName: getIconName(icon.name),
							},
						};
					})
					.reduce((acc, b) => ({ ...acc, ...b })),
			)
			.catch((error) => {
				// eslint-disable-next-line no-console
				console.error(error);
			});
	}
	return objectIconsPromise;
};

// get core icons info as a Promise which returns a Record
export const getCoreIcons: () => Promise<void | {
	[x: string]: NewCoreIconData;
}> = () => {
	return Promise.all(
		Object.keys(coreIconMetadata).map(async (name: string) => {
			const icon = await import(
				/* webpackChunkName: "@atlaskit-internal_[request]" */ `@atlaskit/icon/core/${name}`
			);
			return { name, icon: icon.default };
		}),
	)
		.then((newData) =>
			newData
				.map((icon) => {
					const metadata = coreIconMetadata[icon.name];
					return {
						[icon.name]: {
							key: icon.name,
							keywords: metadata.keywords,
							component: icon.icon,
							componentName: metadata.componentName,
							packageName: metadata.package,
							iconName: getIconName(icon.name),
							categorization: metadata.categorization,
							usage: metadata.usage,
							team: metadata.team,
							status: metadata.status,
							replacement: metadata.replacement,
							shouldRecommendSmallIcon: metadata.shouldRecommendSmallIcon,
						} as NewCoreIconData,
					};
				})
				.reduce((acc, b) => ({ ...acc, ...b })),
		)
		.catch((error) => {
			// eslint-disable-next-line no-console
			console.error(error);
		});
};

// get core icons info as a Promise which returns a Record
export const getIconLabCoreIcons: () => Promise<void | {
	[x: string]: NewCoreIconData;
}> = () => {
	return Promise.all(
		Object.keys(coreIconLabMetadata).map(async (name: string) => {
			const icon = await import(
				/* webpackChunkName: "@atlaskit-internal_[request]" */ `@atlaskit/icon-lab/core/${name}`
			);
			return { name, icon: icon.default };
		}),
	)
		.then((newData) =>
			newData
				.map((icon) => {
					const metadata = coreIconLabMetadata[icon.name];
					return {
						[icon.name]: {
							key: icon.name,
							keywords: metadata.keywords,
							component: icon.icon,
							componentName: metadata.componentName,
							packageName: metadata.package,
							iconName: getIconName(icon.name),
							categorization: metadata.categorization,
							usage: metadata.usage,
							team: metadata.team,
							status: metadata.status,
							replacement: metadata.replacement,
						} as NewCoreIconData,
					};
				})
				.reduce((acc, b) => ({ ...acc, ...b })),
		)
		.catch((error) => {
			// eslint-disable-next-line no-console
			console.error(error);
		});
};

export const getIcon: (
	location: string,
	name: string,
) => Promise<NewCoreIconData | undefined> = async (location: string, name: string) => {
	if (location === '@atlaskit/icon') {
		const icon = await import(
			/* webpackChunkName: "@atlaskit-internal_[request]" */ `@atlaskit/icon/core/${name}`
		);
		const metadata = coreIconMetadata[name];
		const data: NewCoreIconData = {
			key: name,
			keywords: metadata.keywords,
			component: icon.default,
			componentName: metadata.componentName,
			packageName: metadata.package,
			iconName: getIconName(name),
			categorization: metadata.categorization,
			usage: metadata.usage,
			team: metadata.team,
		};
		return data;
	} else if (location === '@atlaskit/icon-lab') {
		const icon = await import(
			/* webpackChunkName: "@atlaskit-internal_[request]" */ `@atlaskit/icon-lab/core/${name}`
		);
		const metadata = coreIconLabMetadata[name];
		const data: NewCoreIconData = {
			key: name,
			keywords: metadata.keywords,
			component: icon.default,
			componentName: metadata.componentName,
			packageName: metadata.package,
			iconName: getIconName(name),
			categorization: metadata.categorization,
			usage: metadata.usage,
			team: metadata.team,
		};
		return data;
	}
};
